import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { API_BASE_URL } from '../../config';

// Async thunks
export const fetchAllUsers = createAsyncThunk(
  'userManagement/fetchAllUsers',
  async ({ page = 1, limit = 20, search = '', role = '', isBanned = null, status = '', refresh = false }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('adminToken');
      console.log('API_BASE_URL:', API_BASE_URL);
      const params = new URLSearchParams({
        page, limit, ...(search && { search }), ...(role && { role }), ...(isBanned !== null && { isBanned }), ...(status && { status }), ...(refresh && { refresh: 'true' })
      });
      const fullUrl = `${API_BASE_URL}/api/website/users?${params}`;
      console.log('Full URL:', fullUrl);
      const response = await axios.get(fullUrl, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch users');
    }
  }
);

// Get user by invite code
export const fetchUserByInviteCode = createAsyncThunk(
  'userManagement/fetchUserByInviteCode',
  async (inviteCode, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API_BASE_URL}/api/website/users/invite-code/${inviteCode}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch user by invite code');
    }
  }
);

export const fetchUserById = createAsyncThunk(
  'userManagement/fetchUserById',
  async (userId, { rejectWithValue }) => {
    console.log('🔄 REDUX: fetchUserById thunk started:', { userId });
    
    try {
      const token = localStorage.getItem('adminToken');
      console.log('🔑 REDUX: Token retrieved:', token ? 'Present' : 'Missing');
      
      console.log('📡 REDUX: Making API call to:', `${API_BASE_URL}/api/website/users/${userId}`);
      const response = await axios.get(`${API_BASE_URL}/api/website/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('✅ REDUX: fetchUserById API call successful:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ REDUX: fetchUserById API call failed:', {
        error: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch user details');
    }
  }
);

export const updateUserRole = createAsyncThunk(
  'userManagement/updateUserRole',
  async ({ userId, newRole, ambassadorContactPhone, ambassadorLocation }, { rejectWithValue }) => {
    console.log('🔄 REDUX: updateUserRole thunk started:', { userId, newRole });
    
    try {
      const token = localStorage.getItem('adminToken');
      console.log('🔑 REDUX: Token retrieved:', token ? 'Present' : 'Missing');
      
      console.log('📡 REDUX: Making API call to:', `${API_BASE_URL}/api/website/users/${userId}/role`);
      const response = await axios.put(`${API_BASE_URL}/api/website/users/${userId}/role`, 
        {
          newRole,
          ...(ambassadorContactPhone !== undefined && { ambassadorContactPhone }),
          ...(ambassadorLocation !== undefined && { ambassadorLocation }),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('✅ REDUX: API call successful:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ REDUX: API call failed:', {
        error: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      return rejectWithValue(error.response?.data?.error || 'Failed to update user role');
    }
  }
);

export const toggleUserBan = createAsyncThunk(
  'userManagement/toggleUserBan',
  async ({ userId, banReason = null }, { rejectWithValue }) => {
    console.log('🔄 REDUX: toggleUserBan thunk started:', { 
      userId, 
      banReason,
      banReasonType: typeof banReason,
      banReasonLength: banReason ? banReason.length : 0,
      banReasonTruthy: !!banReason
    });
    
    try {
      const token = localStorage.getItem('adminToken');
      console.log('🔑 REDUX: Token retrieved:', token ? 'Present' : 'Missing');
      
      const requestBody = { banReason };
      console.log('📤 REDUX: Request body being sent:', requestBody);
      
      console.log('📡 REDUX: Making API call to:', `${API_BASE_URL}/api/website/users/${userId}/ban`);
      const response = await axios.put(`${API_BASE_URL}/api/website/users/${userId}/ban`, 
        requestBody, { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('✅ REDUX: API call successful:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ REDUX: API call failed:', {
        error: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      return rejectWithValue(error.response?.data?.error || 'Failed to toggle user ban status');
    }
  }
);

export const fetchUserTransactions = createAsyncThunk(
  'userManagement/fetchUserTransactions',
  async ({ userId, page = 1, limit = 20, type = 'all', status = 'all', dateFrom = null, dateTo = null }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams({
        page, limit, type, status, ...(dateFrom && { dateFrom }), ...(dateTo && { dateTo })
      });
      const response = await axios.get(`${API_BASE_URL}/api/website/users/${userId}/transactions?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch user transactions');
    }
  }
);





// Initial state
const initialState = {
  users: [],
  usersPagination: { currentPage: 1, totalPages: 0, totalUsers: 0, usersPerPage: 20 },
  usersStatistics: { totalUsers: 0, bannedCount: 0, activeCount: 0, roleStats: [] },
  currentUser: null,
  userTransactions: [],
  transactionsPagination: { currentPage: 1, totalPages: 0, totalTransactions: 0, transactionsPerPage: 20 },

  loading: {
    users: false, currentUser: false, transactions: false,  updateRole: false, toggleBan: false
  },
  errors: {
    users: null, currentUser: null, transactions: null,  updateRole: null, toggleBan: null
  },
  filters: {
    search: '', role: '', isBanned: null, transactionType: 'all', 
    transactionStatus: 'all', dateFrom: null, dateTo: null
  },
  successMessages: { updateRole: null, toggleBan: null }
};

// User management slice
const userManagementSlice = createSlice({
  name: 'userManagement',
  initialState,
  reducers: {
    clearErrors: (state) => {
      state.errors = { users: null, currentUser: null, transactions: null, updateRole: null, toggleBan: null };
    },
    clearSuccessMessages: (state) => {
      state.successMessages = { updateRole: null, toggleBan: null };
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = { search: '', role: '', isBanned: null, transactionType: 'all', transactionStatus: 'all', dateFrom: null, dateTo: null };
    },
    setCurrentUser: (state, action) => {
      state.currentUser = action.payload;
    },
    clearCurrentUser: (state) => {
      state.currentUser = null;
    },
    updateUserInList: (state, action) => {
      const { userId, updates } = action.payload;
      const userIndex = state.users.findIndex(user => user._id === userId);
      if (userIndex !== -1) {
        state.users[userIndex] = { ...state.users[userIndex], ...updates };
      }
    },
    removeUserFromList: (state, action) => {
      const userId = action.payload;
      state.users = state.users.filter(user => user._id !== userId);
    },
    resetUserManagement: () => initialState
  },
  extraReducers: (builder) => {
    builder
      // Fetch all users
      .addCase(fetchAllUsers.pending, (state) => {
        state.loading.users = true;
        state.errors.users = null;
      })
      .addCase(fetchAllUsers.fulfilled, (state, action) => {
        state.loading.users = false;
        state.users = action.payload.users;
        state.usersPagination = action.payload.pagination;
        state.usersStatistics = action.payload.statistics || state.usersStatistics;
        state.errors.users = null;
      })
      .addCase(fetchAllUsers.rejected, (state, action) => {
        state.loading.users = false;
        state.errors.users = action.payload;
      })
      
      // Fetch user by invite code
      .addCase(fetchUserByInviteCode.pending, (state) => {
        state.loading.currentUser = true;
        state.errors.currentUser = null;
      })
      .addCase(fetchUserByInviteCode.fulfilled, (state, action) => {
        state.loading.currentUser = false;
        state.currentUser = action.payload.user;
        state.errors.currentUser = null;
      })
      .addCase(fetchUserByInviteCode.rejected, (state, action) => {
        state.loading.currentUser = false;
        state.errors.currentUser = action.payload;
      })
      
      // Fetch user by ID
      .addCase(fetchUserById.pending, (state) => {
        state.loading.currentUser = true;
        state.errors.currentUser = null;
      })
      .addCase(fetchUserById.fulfilled, (state, action) => {
        state.loading.currentUser = false;
        state.currentUser = action.payload.user;
        state.errors.currentUser = null;
      })
      .addCase(fetchUserById.rejected, (state, action) => {
        state.loading.currentUser = false;
        state.errors.currentUser = action.payload;
      })
      
      // Update user role
      .addCase(updateUserRole.pending, (state) => {
        state.loading.updateRole = true;
        state.errors.updateRole = null;
        state.successMessages.updateRole = null;
      })
      .addCase(updateUserRole.fulfilled, (state, action) => {
        state.loading.updateRole = false;
        state.successMessages.updateRole = action.payload.message;
        state.errors.updateRole = null;
        
        const userIndex = state.users.findIndex(user => user._id === action.payload.user._id);
        if (userIndex !== -1) {
          state.users[userIndex] = { ...state.users[userIndex], ...action.payload.user };
        }
        
        if (state.currentUser && state.currentUser._id === action.payload.user._id) {
          state.currentUser = { ...state.currentUser, ...action.payload.user };
        }
      })
      .addCase(updateUserRole.rejected, (state, action) => {
        state.loading.updateRole = false;
        state.errors.updateRole = action.payload;
        state.successMessages.updateRole = null;
      })
      
      // Toggle user ban
      .addCase(toggleUserBan.pending, (state) => {
        state.loading.toggleBan = true;
        state.errors.toggleBan = null;
        state.successMessages.toggleBan = null;
      })
      .addCase(toggleUserBan.fulfilled, (state, action) => {
        state.loading.toggleBan = false;
        state.successMessages.toggleBan = action.payload.message;
        state.errors.toggleBan = null;
        
        // Update user in list if exists
        const userIndex = state.users.findIndex(user => user._id === action.payload.user._id);
        if (userIndex !== -1) {
          state.users[userIndex] = action.payload.user;
        }
        
        // Update current user if it's the same user
        if (state.currentUser && state.currentUser._id === action.payload.user._id) {
          state.currentUser = action.payload.user;
        }
      })
      .addCase(toggleUserBan.rejected, (state, action) => {
        state.loading.toggleBan = false;
        state.errors.toggleBan = action.payload;
        state.successMessages.toggleBan = null;
      })
      
      // Fetch user transactions
      .addCase(fetchUserTransactions.pending, (state) => {
        state.loading.transactions = true;
        state.errors.transactions = null;
      })
      .addCase(fetchUserTransactions.fulfilled, (state, action) => {
        state.loading.transactions = false;
        state.userTransactions = action.payload.transactions;
        state.transactionsPagination = action.payload.pagination;
        state.errors.transactions = null;
      })
      .addCase(fetchUserTransactions.rejected, (state, action) => {
        state.loading.transactions = false;
        state.errors.transactions = action.payload;
      })
      

  }
});

// Export actions
export const {
  clearErrors, clearSuccessMessages, setFilters, clearFilters, setCurrentUser, 
  clearCurrentUser, updateUserInList, removeUserFromList, resetUserManagement
} = userManagementSlice.actions;

// Export selectors
export const selectUserManagement = (state) => state.userManagement;
export const selectUsers = (state) => state.userManagement.users;
export const selectUsersPagination = (state) => state.userManagement.usersPagination;
export const selectUsersStatistics = (state) => state.userManagement.usersStatistics;
export const selectCurrentUser = (state) => state.userManagement.currentUser;
export const selectUserTransactions = (state) => state.userManagement.userTransactions;
export const selectTransactionsPagination = (state) => state.userManagement.transactionsPagination;

export const selectLoading = (state) => state.userManagement.loading;
export const selectErrors = (state) => state.userManagement.errors;
export const selectFilters = (state) => state.userManagement.filters;
export const selectSuccessMessages = (state) => state.userManagement.successMessages;

// Export reducer
export default userManagementSlice.reducer;
